import type { AppointmentAuthority } from '@munifocus/access';

/**
 * The local-government registry, as the Government Portal reads it.
 *
 * The registry itself is administered from the Admin Portal — created, edited,
 * officers appointed — and `@munifocus/contracts-admin` owns those write
 * shapes. Everything here is read-only on purpose: an officer consumes the
 * registry (which local government is this file's, who may be assigned to it,
 * what prints on the letterhead) and never edits it.
 *
 * The string unions are duplicated from the admin contracts rather than
 * imported: the two portals are versioned and deployed apart, and a shared
 * union would couple the officer client to the admin client's release. Keep
 * both edits together — they describe the same columns.
 */

/** The five statutory classes under New Brunswick's Local Governance Act. */
export const GOV_LOCAL_GOVERNMENT_TYPES = [
  'city',
  'town',
  'village',
  'rural_community',
  'regional_municipality',
] as const;

export type GovLocalGovernmentType = (typeof GOV_LOCAL_GOVERNMENT_TYPES)[number];

/** A person authorised to sign municipal enforcement documents. */
export interface GovSigningOfficer {
  name: string;
  title: string;
}

/** Which Government Portal surface an appointee gets. */
export type GovAppointmentAccessType = 'officer' | 'staff';

/**
 * Enabling statutes an officer may be appointed under.
 *
 * An alias of the one definition in `@munifocus/access` rather than a fourth
 * copy of the codes. The distinct name is kept because callers import it; what
 * is not kept is a second opinion about what the codes are.
 */
export type GovAppointmentAuthority = AppointmentAuthority;

/**
 * One registry row — the document-assembly profile for a local government.
 *
 * This is what makes a generated notice the local government's own document
 * rather than a generic template: `officialNameEn` / `officialNameFr` are the
 * two columns of the bilingual letterhead, and the clerk / signing officers
 * fill the signature block.
 *
 * The head of council is deliberately absent. It used to be carried here as
 * `mayorName` / `deputyMayorName` and was never printed — no approved Word
 * template has a line for it — so `OfficialsRoster` moved the roster to
 * `council_member` and dropped the two columns. A template that grows such a
 * line reads the roster.
 */
export interface LocalGovernmentProfile {
  id: string;
  /** Common name, e.g. "Dieppe" — what an officer picks from a list. */
  name: string;
  /** Letterhead name, English column. Falls back to `name` when blank. */
  officialNameEn: string;
  /** Letterhead name, French column. Falls back to `name` when blank. */
  officialNameFr: string;
  /**
   * Pre-amalgamation names its land is still recorded under on SNB Parcel
   * records. Read by the intake's parcel-location check, never printed.
   */
  formerNames: string[];
  province: string;
  type: GovLocalGovernmentType;
  streetAddress: string;
  postalCode: string;
  phone: string;
  /** General office email; statutory correspondence goes to the clerk. */
  email: string;
  clerkName: string;
  clerkEmail: string;
  /**
   * Short-lived signed URL for the municipality's logo, or null.
   *
   * A URL rather than a key because no client can sign one, and it is
   * re-signed on every read rather than stored — which is also why nothing
   * should cache this field beyond the response it arrived in.
   */
  logoUrl: string | null;
  /** The roster's flagged officials, in roster order. The first one signs. */
  signingOfficers: GovSigningOfficer[];
}

/**
 * An operator holding an appointment at a local government — the source the
 * file-assignment picker draws from.
 *
 * Both access types are returned. Staff (clerk, council, finance) are not
 * assignable as the working officer, but they are who a file gets routed to for
 * external action, so the caller needs to see them and decide; `accessType`
 * is what it decides on.
 */
export interface AppointedOfficer {
  /** Short-lived signed URL for the officer's profile photo, or null. */
  avatarUrl?: string | null;
  /** `app_user.id` — the value that goes in `assignedOfficerId`. */
  userId: string;
  appointmentId: string;
  displayName: string;
  email: string;
  accessType: GovAppointmentAccessType;
  authorities: GovAppointmentAuthority[];
  /** Municipal badge / PIC number, as printed on generated documents. */
  badgeNumber: string;
  appointedOn: string | null;
  /** Null for an open-ended appointment. */
  expiresOn: string | null;
}

/** Why an appointee cannot be the working officer on a file. */
export type OfficerIneligibility = 'staff_access' | 'appointment_expired';

/**
 * New Brunswick. Appointments are made under provincial statute, so "the day it
 * expires" is a day there, not a day in UTC.
 */
const NB_TIME_ZONE = 'America/Moncton';

/** The calendar day it currently is in New Brunswick, as `YYYY-MM-DD`. */
function nbCalendarDay(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NB_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * Whether an appointment is still running on a given day.
 *
 * `expiresOn` is a date, not an instant: an appointment that expires on the
 * 30th is good through the 30th. So this compares *calendar days in New
 * Brunswick* rather than doing offset arithmetic — the province runs AST in
 * winter and ADT in summer, and any fixed offset retires appointments early or
 * late by an hour twice a year. Comparing days is exactly the rule, with no
 * arithmetic to get wrong.
 *
 * Shared rather than re-derived per caller because every surface that asks
 * "may this operator hold this file?" has to give the same answer — see #541,
 * where the deadline stops being advisory.
 */
export function isAppointmentCurrent(
  officer: Pick<AppointedOfficer, 'expiresOn'>,
  now: Date = new Date(),
): boolean {
  if (!officer.expiresOn) return true;
  const expiry = officer.expiresOn.slice(0, 10);
  // An unparseable value is not authority. Better to ask an administrator to
  // fix the row than to treat a malformed date as an open-ended term.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(expiry)) return false;
  return nbCalendarDay(now) <= expiry;
}

/**
 * Why this appointee cannot be assigned a file, or null when they can.
 *
 * Staff are returned by the roster on purpose — they are who a file is routed to
 * for external action — so the caller has to be able to say *why* the name it is
 * showing is not selectable, rather than quietly omitting it and leaving an
 * officer hunting for a colleague who is plainly appointed.
 */
export function officerIneligibility(
  officer: Pick<AppointedOfficer, 'accessType' | 'expiresOn'>,
  now: Date = new Date(),
): OfficerIneligibility | null {
  if (officer.accessType !== 'officer') return 'staff_access';
  if (!isAppointmentCurrent(officer, now)) return 'appointment_expired';
  return null;
}
